import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { PawPrint, Mail, Lock, User, Building2, ArrowLeft, ArrowUpRight, Heart, ShieldAlert, Siren, AlertCircle } from "lucide-react";
import { C, DISPLAY, BODY, MONO, EBtn } from "@/styles/editorial";

const B = {
  border: `2px solid ${C.navy}`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

const NAV_LINKS = [
  { l: "Hub", h: "/" },
  { l: "Recursos", h: "/recursos/ciudadania/guias" },
  { l: "Prepara", h: "/prepara" },
  { l: "Aprende", h: "/aprende" },
  { l: "Fauna", h: "/fauna" },
  { l: "Blog", h: "/blog" },
];

const AUTH_PANELS = {
  particular: {
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/7rlk23xc_e3576108-b272-451b-998e-82921d51dbe5.webp",
    alt: "Voluntaria abrazando a un perro en un refugio",
    label: "Conecta — La red solidaria",
    quote: <>Cuidar también<br />es prepararse.</>,
    text: "Protectoras, voluntariado y ciudadanía, coordinados para que ningún animal quede atrás.",
  },
  protectora: {
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/rtypyck4_Untitled%20design.webp",
    alt: "Cuidadora sonriendo con un gato en brazos en un refugio",
    label: "Conecta — Red de protectoras",
    quote: <>La solidaridad<br />necesita organización.</>,
    text: "Publica las necesidades de tu protectora, refugio, santuario o colonia felina y recibe la ayuda del voluntariado de forma ordenada.",
  },
  emergencias: {
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/7rlk23xc_e3576108-b272-451b-998e-82921d51dbe5.webp",
    alt: "Voluntaria abrazando a un perro en un refugio",
    label: "Conecta — Personal de emergencias",
    quote: <>Ningún animal<br />debería quedar atrás.</>,
    text: "Coordinación entre equipos de emergencia, protectoras y voluntariado cuando cada minuto cuenta.",
  },
};

const EMERGENCY_CORPS = [
  { value: "proteccion_civil", label: "Protección Civil" },
  { value: "bomberos", label: "Bomberos" },
  { value: "policia_nacional", label: "Policía Nacional" },
  { value: "policia_local", label: "Policía Local" },
  { value: "guardia_civil", label: "Guardia Civil" },
  { value: "mossos", label: "Mossos d'Esquadra" },
  { value: "ertzaintza", label: "Ertzaintza" },
  { value: "policia_foral", label: "Policía Foral" },
  { value: "agents_rurals", label: "Agents Rurals / Forestales" },
  { value: "sanitarios", label: "Sanitarios / SAMUR" },
  { value: "cruz_roja", label: "Cruz Roja" },
  { value: "veterinarios", label: "Veterinarios de Emergencias" },
  { value: "otro", label: "Otro personal de emergencias" },
];

const inputStyle = { border: `2px solid ${C.navy}`, borderRadius: 2, background: "#fff", fontFamily: B.mono, fontSize: "0.875rem" };
const selectStyle = { border: `2px solid ${C.navy}`, borderRadius: 2, background: "#fff" };
const dialogStyle = { borderRadius: 2, border: B.border, boxShadow: "none" };

export default function AuthPage() {
  const navigate = useNavigate();
  const { user, setUser, setActiveRole } = useAuth();
  const pendingRole = localStorage.getItem("ea_pending_auth_role");
  const [isLogin, setIsLogin] = useState(pendingRole ? false : true);
  const [role, setRole] = useState(pendingRole || "particular");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    organization_name: "",
    emergency_corps: "",
    emergency_display_mode: "corps_only",
  });
  
  // State for "email already exists" flow
  const [showRoleRequestDialog, setShowRoleRequestDialog] = useState(false);
  const [existingUserInfo, setExistingUserInfo] = useState(null);

  if (user) {
    const redirectPath = user.role === "protectora" ? "/shelter" : user.role === "emergencias" ? "/emergency-dashboard" : "/dashboard";
    navigate(redirectPath, { replace: true });
    return null;
  }

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? "login" : "register";
      let payload;
      if (isLogin) {
        payload = { email: form.email, password: form.password };
      } else {
        payload = {
          email: form.email,
          password: form.password,
          name: form.name,
          role,
          organization_name: role === "protectora" ? form.organization_name : undefined,
          emergency_corps: role === "emergencias" ? form.emergency_corps : undefined,
          emergency_display_mode: role === "emergencias" ? form.emergency_display_mode : undefined,
        };
      }
      const res = await axios.post(`${API}/auth/${endpoint}`, payload, { withCredentials: true });
      const { token, user: userData } = res.data;
      localStorage.setItem("ea_token", token);
      localStorage.setItem("ea_user", JSON.stringify(userData));
      localStorage.removeItem("ea_pending_auth_role");
      setUser(userData);
      setActiveRole(userData.role || "particular");
      
      if (!isLogin && userData.role === "emergencias") {
        toast.success("Cuenta creada! Tu verificación está pendiente de aprobación.", { duration: 5000 });
      } else {
        toast.success(isLogin ? "Bienvenido de vuelta!" : "Cuenta creada!");
      }
      
      const redirect = localStorage.getItem("ea_redirect_after_auth");
      if (redirect) {
        localStorage.removeItem("ea_redirect_after_auth");
        navigate(redirect);
      } else {
        const redirectPath = userData.role === "protectora" ? "/shelter" : userData.role === "emergencias" ? "/emergency-dashboard" : "/dashboard";
        navigate(redirectPath);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "";
      
      // Check if email already exists and user wants emergency role
      if (errorMsg.includes("ya esta registrado") && role === "emergencias" && !isLogin) {
        // Check user info
        try {
          const checkRes = await axios.get(`${API}/auth/check-email/${encodeURIComponent(form.email)}`);
          if (checkRes.data.exists) {
            setExistingUserInfo(checkRes.data);
            setShowRoleRequestDialog(true);
          }
        } catch {
          toast.error("Error al verificar el email");
        }
      } else {
        toast.error(errorMsg || "Error de autenticación");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEmergencyRole = async () => {
    // For Google Auth users, password is not required
    if (!existingUserInfo?.uses_google_auth && !form.password) {
      toast.error("Introduce tu contraseña actual para confirmar");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-emergency-role`, {
        email: form.email,
        password: existingUserInfo?.uses_google_auth ? null : form.password,
        emergency_corps: form.emergency_corps,
        emergency_display_mode: form.emergency_display_mode,
      });
      toast.success("¡Solicitud enviada! Te notificaremos cuando sea revisada.", { duration: 5000 });
      setShowRoleRequestDialog(false);
      // Switch to login mode
      setIsLogin(true);
      setRole("particular");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar solicitud");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    localStorage.setItem("ea_pending_role", role);
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const getRoleLabel = (r) => {
    if (r === "protectora") return "PROTECTORA";
    if (r === "emergencias") return "PERSONAL DE EMERGENCIAS";
    return "VOLUNTARIO";
  };

  const panel = (!isLogin && AUTH_PANELS[role]) || AUTH_PANELS.particular;

  return (
    <div className="min-h-screen flex flex-col" data-testid="auth-page" style={{ background: C.offwhite, fontFamily: BODY }}>
      {/* NAV — misma estructura que /conecta */}
      <header className="sticky top-0 z-50 px-6 md:px-12 py-3" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/conecta")} className="flex items-center gap-2"
              style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="auth-home-logo">
              <img src="/logos/logo-conecta.png" alt="Conecta" className="w-7 h-7 object-contain" />
              <span className="text-sm font-bold tracking-tight" style={{ fontFamily: DISPLAY, color: C.white }}>Conecta</span>
            </button>
            <div className="h-4 w-px hidden md:block" style={{ background: "rgba(255,255,255,0.15)" }} />
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((lnk) => (
                <button key={lnk.l} onClick={() => navigate(lnk.h)}
                  className="px-3 py-1.5 text-[11px] font-semibold tracking-wider uppercase transition-all"
                  style={{ fontFamily: MONO, color: "rgba(255,255,255,0.55)", background: "none", border: "none", cursor: "pointer" }}
                  data-testid={`auth-nav-${lnk.l.toLowerCase()}`}
                >{lnk.l}</button>
              ))}
            </nav>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase"
            style={{ fontFamily: MONO, color: C.orange, background: "none", border: "none", cursor: "pointer" }} data-testid="auth-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Volver
          </button>
        </div>
      </header>

      {/* Split editorial: foto + formulario */}
      <div className="flex-1 grid lg:grid-cols-2">
        {/* Panel fotográfico */}
        <div className="relative hidden lg:block" data-testid="auth-photo-panel">
          <img
            src={panel.photo}
            alt={panel.alt}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(41,41,77,0.85) 0%, rgba(41,41,77,0.25) 45%, rgba(41,41,77,0.1) 100%)" }} />
          <div className="absolute bottom-0 left-0 right-0 p-10 xl:p-14">
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-4" style={{ fontFamily: MONO, color: C.orange }}>
              {panel.label}
            </p>
            <p className="text-[clamp(1.8rem,2.8vw,2.8rem)] leading-[1.05] mb-4" style={{ fontFamily: DISPLAY, color: C.white }}>
              {panel.quote}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.75)", maxWidth: "42ch" }}>
              {panel.text}
            </p>
          </div>
          <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-2" style={{ background: C.orangeRed }} />
        </div>

        {/* Columna de formulario */}
        <div className="flex items-start lg:items-center justify-center px-6 py-12 lg:py-16">
          <div className="w-full max-w-md">
            {/* Cabecera editorial */}
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ fontFamily: MONO, color: C.blue }}>
              {isLogin ? "Acceso" : "Registro"} — Emergencia Animal
            </p>
            <h1 className="text-[clamp(2.2rem,4vw,3rem)] leading-[0.95] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.navy }}>
              {isLogin ? "Hola de nuevo" : role === "protectora" ? "Registra tu entidad" : "Únete a la red"}
            </h1>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(41,41,77,0.65)", maxWidth: "48ch" }}>
              {isLogin
                ? "Accede a tu espacio personal para ver las necesidades activas y tus ofrecimientos."
                : role === "protectora"
                ? "Protectoras, refugios, santuarios y colonias felinas. Vamos paso a paso: crear la cuenta es gratuito y podrás completar el perfil de tu entidad más tarde."
                : "Vamos paso a paso: crear tu cuenta es gratuito y lleva menos de un minuto. Podrás completar tu perfil más tarde."}
            </p>
            <div aria-hidden="true" className="h-1 w-16 mb-8" style={{ background: C.orangeRed }} />

            <div>
            {/* Role tabs for register */}
            {!isLogin && (
              <div className="mb-6">
                <Label className="text-[10px] font-bold tracking-[0.25em] uppercase mb-2 block" style={{ fontFamily: B.mono, color: C.navy }}>Soy...</Label>
                <div className="flex flex-col gap-0">
                  {[
                    { val: "particular", label: "VOLUNTARIO/A", icon: User, color: "#686af3", testId: "role-particular-tab" },
                    { val: "protectora", label: "PROTECTORA · REFUGIO · SANTUARIO", icon: Building2, color: "#81801a", testId: "role-protectora-tab" },
                    { val: "emergencias", label: "PERSONAL EMERGENCIAS", icon: Siren, color: "#f64e0e", testId: "role-emergencias-tab" },
                  ].map((r) => (
                    <button
                      key={r.val}
                      type="button"
                      onClick={() => setRole(r.val)}
                      data-testid={r.testId}
                      className="flex items-center gap-3 px-4 py-3 text-[10px] font-bold uppercase tracking-wider transition-colors text-left"
                      style={{
                        fontFamily: B.mono,
                        background: role === r.val ? r.color : "#fff",
                        color: role === r.val ? "#fff" : C.navy,
                        border: `2px solid ${C.navy}`,
                        borderLeft: `8px solid ${r.color}`,
                        marginTop: r.val !== "particular" ? "-2px" : 0,
                      }}
                    >
                      <r.icon className="w-4 h-4 flex-shrink-0" />{r.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                      {role === "protectora" ? "NOMBRE CONTACTO" : "NOMBRE"}
                    </Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#686af3" }} />
                      <Input id="name" name="name" value={form.name} onChange={handleChange} placeholder="Tu nombre" className="pl-10" style={inputStyle} required data-testid="input-name" />
                    </div>
                  </div>
                  
                  {role === "protectora" && (
                    <div>
                      <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>PROTECTORA</Label>
                      <div className="relative mt-1">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#686af3" }} />
                        <Input id="organization_name" name="organization_name" value={form.organization_name} onChange={handleChange} placeholder="Nombre de tu organización" className="pl-10" style={inputStyle} required data-testid="input-org-name" />
                      </div>
                    </div>
                  )}
                  
                  {role === "emergencias" && (
                    <>
                      <div>
                        <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CUERPO / ORGANIZACIÓN</Label>
                        <Select value={form.emergency_corps} onValueChange={(v) => setForm({ ...form, emergency_corps: v })}>
                          <SelectTrigger className="mt-1" style={selectStyle} data-testid="select-corps">
                            <SelectValue placeholder="Selecciona tu cuerpo..." />
                          </SelectTrigger>
                          <SelectContent style={{ borderRadius: 4, border: B.border }}>
                            {EMERGENCY_CORPS.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>¿CÓMO QUIERES APARECER?</Label>
                        <Select value={form.emergency_display_mode} onValueChange={(v) => setForm({ ...form, emergency_display_mode: v })}>
                          <SelectTrigger className="mt-1" style={selectStyle} data-testid="select-display-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent style={{ borderRadius: 4, border: B.border }}>
                            <SelectItem value="corps_only">Personal de Emergencias - [Mi cuerpo]</SelectItem>
                            <SelectItem value="anonymous">Personal de Emergencias (anónimo)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: "#686af3" }}>
                          // TU NOMBRE NUNCA SERÁ VISIBLE
                        </p>
                      </div>
                      <div className="p-3" style={{ background: "#f64e0e15", border: "1px solid #f64e0e", borderRadius: 4 }}>
                        <p className="text-[10px] font-bold tracking-wider flex items-center gap-1.5" style={{ fontFamily: B.mono, color: "#f64e0e" }}>
                          <ShieldAlert className="w-3.5 h-3.5" /> VERIFICACIÓN REQUERIDA
                        </p>
                        <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: "#666" }}>
                          Tu cuenta será revisada por nuestro equipo antes de poder publicar. Recibirás una notificación cuando esté aprobada.
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>EMAIL</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#686af3" }} />
                  <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="tu@email.com" className="pl-10" style={inputStyle} required data-testid="input-email" />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CONTRASEÑA</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#686af3" }} />
                  <Input id="password" name="password" type="password" value={form.password} onChange={handleChange} placeholder="********" className="pl-10" style={inputStyle} required data-testid="input-password" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || (!isLogin && role === "emergencias" && !form.emergency_corps)}
                className="w-full py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-50"
                style={{ fontFamily: B.font, background: role === "emergencias" && !isLogin ? "#f64e0e" : "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                data-testid="auth-submit-btn"
              >
                {loading ? "CARGANDO..." : isLogin ? "INICIAR SESIÓN" : role === "emergencias" ? "SOLICITAR VERIFICACIÓN" : "CREAR CUENTA"}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: "1px solid rgba(45,53,97,0.15)" }} /></div>
              <div className="relative flex justify-center">
                <span className="px-3 text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: C.navy, background: C.offwhite }}>O CONTINÚA CON</span>
              </div>
            </div>

            {/* Google Auth */}
            <button
              type="button"
              onClick={handleGoogleAuth}
              className="w-full py-3 flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
              style={{ fontFamily: B.font, background: "#fff", color: "#2D3561", border: B.border, boxShadow: B.shadowSm }}
              data-testid="google-auth-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              GOOGLE
            </button>

            {/* Toggle */}
            <p className="text-center mt-5 text-sm" style={{ color: "#2D3561" }}>
              {isLogin ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
              <button type="button" onClick={() => setIsLogin(!isLogin)} className="font-bold uppercase tracking-wider underline" style={{ color: "#686af3" }} data-testid="toggle-auth-mode">
                {isLogin ? "REGÍSTRATE" : "INICIA SESIÓN"}
              </button>
            </p>
          </div>

          {/* Footer note */}
          <p className="text-center mt-8 text-xs" style={{ fontFamily: B.mono, color: C.navy }}>
            // NADIE SE QUEDA ATRÁS <Heart className="w-3 h-3 inline fill-current" style={{ color: "#e8637a" }} />
          </p>
          </div>
        </div>
      </div>

      {/* Dialog for existing user requesting emergency role */}
      <Dialog open={showRoleRequestDialog} onOpenChange={setShowRoleRequestDialog}>
        <DialogContent className="sm:max-w-md" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#f64e0e", border: "1px solid rgba(45,53,97,0.08)" }}>
                <AlertCircle className="w-4 h-4 text-white" />
              </div>
              ¡YA TIENES CUENTA!
            </DialogTitle>
            <DialogDescription style={{ fontFamily: B.mono, fontSize: "11px", color: "#666" }}>
              El email ya está registrado como {getRoleLabel(existingUserInfo?.role)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="p-3" style={{ background: "#686af315", border: "1px solid #686af3", borderRadius: 4 }}>
              <p className="text-sm" style={{ color: "#2D3561" }}>
                ¿Quieres <strong>solicitar el rol de Personal de Emergencias</strong> para tu cuenta existente?
              </p>
              <p className="text-[10px] mt-2" style={{ fontFamily: B.mono, color: "#686af3" }}>
                // TU CUENTA ACTUAL SEGUIRÁ FUNCIONANDO MIENTRAS SE REVISA LA SOLICITUD
              </p>
            </div>
            
            {existingUserInfo?.has_emergency_request && (
              <div className="p-3" style={{ background: "#fa900015", border: "1px solid #fa9000", borderRadius: 4 }}>
                <p className="text-xs font-bold" style={{ fontFamily: B.mono, color: "#fa9000" }}>
                  YA TIENES UNA SOLICITUD PENDIENTE
                </p>
                <p className="text-[10px] mt-1" style={{ color: "#666" }}>
                  Te notificaremos cuando sea revisada.
                </p>
              </div>
            )}
            
            {!existingUserInfo?.has_emergency_request && (
              <>
                {!existingUserInfo?.uses_google_auth ? (
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CONFIRMA TU CONTRASEÑA</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#686af3" }} />
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="Tu contraseña actual"
                        className="pl-10"
                        style={inputStyle}
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-3" style={{ background: "#8B9A2D15", border: "1px solid #8B9A2D", borderRadius: 4 }}>
                    <p className="text-xs" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>
                      ✓ Identificado con Google Auth
                    </p>
                  </div>
                )}
                
                <div className="p-3" style={{ background: "#f8f7f4", border: "1px solid rgba(45,53,97,0.08)" }}>
                  <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#686af3" }}>TU SOLICITUD</p>
                  <p className="text-sm mt-1" style={{ color: "#2D3561" }}>
                    <strong>Cuerpo:</strong> {EMERGENCY_CORPS.find(c => c.value === form.emergency_corps)?.label || "No seleccionado"}
                  </p>
                  <p className="text-sm" style={{ color: "#2D3561" }}>
                    <strong>Aparecerás como:</strong> {form.emergency_display_mode === "anonymous" ? "Personal de Emergencias" : `Personal de Emergencias - ${EMERGENCY_CORPS.find(c => c.value === form.emergency_corps)?.label || "..."}`}
                  </p>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="gap-2 mt-2">
            <button
              type="button"
              onClick={() => setShowRoleRequestDialog(false)}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider"
              style={{ fontFamily: B.mono, background: "#fff", color: "#2D3561", border: B.border }}
            >
              CANCELAR
            </button>
            {!existingUserInfo?.has_emergency_request ? (
              <button
                type="button"
                onClick={handleRequestEmergencyRole}
                disabled={loading || (!existingUserInfo?.uses_google_auth && !form.password)}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-50"
                style={{ fontFamily: B.mono, background: "#f64e0e", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                data-testid="confirm-role-request-btn"
              >
                {loading ? "ENVIANDO..." : "SOLICITAR CAMBIO DE ROL"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { setShowRoleRequestDialog(false); setIsLogin(true); }}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider"
                style={{ fontFamily: B.mono, background: "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
              >
                INICIAR SESIÓN
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
